import { Container, Typography } from '@evocolabs/orkaui'
import { Box, BoxProps, Stack, useMediaQuery } from '@mui/material'
import Image, { ImageProps } from 'next/image'
import { useCallback, useEffect, useState } from 'react'
import { getImage } from '@/src/utils/media'
import theme from '@/src/theme'
import Weight48 from '@evocolabs/icons/lib/Weight48'
import Time64 from '@evocolabs/icons/lib/Time64'
import controls from '@/src/lottie/product/orka-two/elevated controls.json'
import Lottie from 'react-lottie'
import styles from './styles.module.scss'
import ImageCard from '../ImageCard'
import { ContentTitle, SectionTitle } from '../title'

const SRC_PATH = 'website/explore-orka-two'

const data = {
  title: 'User-Friendly Design for Effortless Comfort',
  cardData: [
    {
      title: 'Small and Light',
      content: `Crafted for comfort, Orka Two’s compact design ensures all-day wear without fatigue. You will forget that you're even wearing them.`,
      activeContent: (
        <Box
          display="flex"
          mt={{ xs: 0, dl: 1 }}
          alignItems="center"
          gap="12px"
        >
          <Weight48 size={48} />
          <Typography variant="Body1" color={theme.palette.primary[700]}>
            0.1 ounces (3.4 g)
          </Typography>
        </Box>
      ),

      src: 'lightweight.png'
    },
    {
      title: 'Always On-the-Go Rechargeable Battery',
      content:
        'With the portable charging case, you are always ready to be on the go. Enjoy uninterrupted clarity without the hassle of battery replacement.',
      activeContent: (
        <Box
          display="flex"
          mt={{ xs: 0, dl: 1 }}
          alignItems="center"
          gap="12px"
        >
          <Time64
            size={48}
            className={styles['time-icon']}
            replaceFillColor={[['#F2E9DA', 'transparent']]}
          />
          <Typography variant="Body1" color={theme.palette.primary[700]}>
            36 Hours with Charging Case
          </Typography>
        </Box>
      ),
      src: 'rechargeable.png'
    },
    {
      title: 'Convenient On-Case Volume Control',
      content: `Say goodbye to smartphone app dependence for hearing aid control. Orka Two’s on-case controls ensure quick, seamless adjustments.`,
      src: 'volume_control.png',
      imageProps: {
        style: { borderRadius: '12px', objectFit: 'contain' }
      } as ImageProps
    }
  ]
}

function VolumeControl(props: BoxProps) {
  const options = {
    loop: true,
    autoplay: true,
    animationData: controls,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  }
  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '50%',
        maxWidth: '400px',
        maxHeight: '400px',
        m: 'auto',
        overflow: 'hidden'
      }}
      {...props}
    >
      <Lottie style={{ width: '100%' }} options={options} />
    </Box>
  )
}

function AfterSalesMobile() {
  const { cardData } = data
  return (
    <Stack spacing={5} alignItems="center">
      {cardData
        .slice(0, 2)
        .map(({ src, title, content, imageProps, activeContent, ...props }) => (
          <ImageCard
            key={`${title}-mobile`}
            title={title}
            src={getImage(src, 'PC', SRC_PATH)}
            imageProps={imageProps}
            maxWidth="400px"
            {...props}
          >
            <Stack gap={2}>
              <ContentTitle textAlign="left">{title}</ContentTitle>
              <Typography variant="body2">{content}</Typography>
              {activeContent}
            </Stack>
          </ImageCard>
        ))}
      <Stack maxWidth="400px">
        <VolumeControl />
        <ContentTitle textAlign="left" mt={3}>
          {cardData[2].title}
        </ContentTitle>
        <Typography mt={1} variant="body2">
          {cardData[2].content}
        </Typography>
      </Stack>
    </Stack>
  )
}

function AfterSalesDesktop() {
  const { cardData } = data
  const [currPos, setCurrPos] = useState<number>(2)
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null)

  const cleanUpTimer = useCallback(() => {
    if (!intervalId) return
    clearInterval(intervalId)
    setIntervalId(null)
  }, [intervalId])

  const setUpTimer = useCallback(() => {
    const id = setInterval(() => {
      setCurrPos((prev) => (prev + 1) % 3)
    }, 3000)
    setIntervalId(id)
  }, [])

  const handleSelect = (itemIndex: number) => {
    cleanUpTimer()
    setCurrPos(itemIndex)
    setUpTimer()
  }

  useEffect(() => {
    setUpTimer()
    return cleanUpTimer
  }, [])

  return (
    <Stack direction="row" spacing={8} mt={6}>
      <Stack flex="1 1 0" spacing={5} alignSelf="center">
        {cardData.map(({ title, content, activeContent }, index) => (
          <Box
            key={`${title}-desktop`}
            onClick={() => handleSelect(index)}
            sx={{
              display: 'flex',
              cursor: 'pointer',
              gap: '16px',
              paddingBottom: currPos === index && !activeContent ? 7 : 0
            }}
          >
            <Box>
              <Box
                className={`${styles.tab} ${
                  currPos === index ? styles.tab_active : ''
                }`}
              />
            </Box>
            <Stack>
              <ContentTitle
                textAlign="left"
                style={{
                  color: theme.palette.grey[currPos === index ? 800 : 400]
                }}
              >
                {title}
              </ContentTitle>
              <Typography
                mt={1}
                variant="body2"
                style={{
                  color: theme.palette.grey[currPos === index ? 700 : 400]
                }}
              >
                {content}
              </Typography>
              {currPos === index && activeContent ? activeContent : null}
            </Stack>
          </Box>
        ))}
      </Stack>
      <Box
        position="relative"
        paddingBottom={3 / 4}
        maxHeight="400px"
        flex="1 1 0"
      >
        {cardData.slice(0, 2).map(({ src, title, imageProps }, index) => (
          <Image
            src={getImage(src, 'PC', SRC_PATH)}
            alt={title}
            key={title}
            fill
            sizes="(min-width: 1024px) 100%"
            unoptimized
            style={{
              borderRadius: '12px',
              objectFit: imageProps?.style?.objectFit || 'cover',
              display: currPos === index ? 'block' : 'none'
            }}
          />
        ))}
        <VolumeControl display={currPos === 2 ? 'block' : 'none'} />
      </Box>
    </Stack>
  )
}

function Design() {
  const isMobile = useMediaQuery('(max-width: 1024px)')

  return (
    <Box bgcolor={theme.palette.secondary[100]}>
      <Container
        sx={{
          paddingY: { xs: 5, dl: 10 }
        }}
      >
        <SectionTitle mb={{ xs: 5, dl: 6 }}>{data.title}</SectionTitle>
        {isMobile ? <AfterSalesMobile /> : <AfterSalesDesktop />}
      </Container>
    </Box>
  )
}

export default Design
